import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type MultiColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { FiveNumberSummary } from './five-number-summary';
import { calculateBoxPlotStats } from '../../../lib/stats';
import { BoxPlotStats } from '../../../lib/stats/types';

interface FiveNumberSummaryControlValues {
  [ControlIds.Values]: string[];
}

interface ExtendedBoxPlotStats extends BoxPlotStats {
  data: number[];
}

const valuesControl: MultiColumnSelectControl = {
  id: ControlIds.Values,
  type: ControlType.MultiColumnSelect,
  label: 'Select data column',
  required: true,
  numericOnly: true,
  maxSelections: 1,
  defaultValue: ['Values']
};

export const fiveNumberSummaryControls = {
  tabs: [
    {
      title: 'Data',
      controls: [valuesControl]
    }
  ]
};

export const fiveNumberSummaryConfig: CalculatorConfig<CalculatorId.FIVE_NUMBER_SUMMARY> = {
  id: CalculatorId.FIVE_NUMBER_SUMMARY,
  type: CalculatorType.Graph,
  name: 'Five Number Summary',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'Values', 
        name: 'Values', 
        values: [
          '15', '18', '19', '20', '21', '22', '23', '25', '28', 
          '30', '31', '32', '35', '38', '40', '42', '45', '48',
          '50', '55', '58', '60', '65', '72', '85'
        ]
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: ['Values']
  },
  controls: fiveNumberSummaryControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns
      .filter(col => col.values.every(v => !isNaN(Number(v))))
      .map(col => col.id)
      .filter(Boolean); // Filter out any undefined values
    
    // Return the first numeric column if available, otherwise empty array
    return {
      [ControlIds.Values]: numericColumns.length > 0 ? [numericColumns[0]] : []
    } as Partial<FiveNumberSummaryControlValues>;
  },
  getVisualizationData: ({ data, controls }): ExtendedBoxPlotStats | null => {
    const selectedColumns = controls[ControlIds.Values] as string[];
    if (!selectedColumns.length) return null;

    const column = data.columns.find(col => col.id === selectedColumns[0]);
    if (!column) return null;

    const values = column.values
      .map(v => Number(v))
      .filter(v => !isNaN(v));
    if (!values.length) return null;

    const stats = calculateBoxPlotStats(values);
    return {
      ...stats,
      data: values
    };
  },
  render: ({ visualizationData }) => <FiveNumberSummary visualizationData={visualizationData as ExtendedBoxPlotStats} />
}; 